import { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/appointments';
import { format, startOfDay, addDays, parseISO, isBefore } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Settings, Search } from 'lucide-react';
import {
  ViewToggle,
  DateRibbon,
  AppointmentFilters,
  CalendarView,
  TableView,
  SlotsView,
  AppointmentDetailSheet,
  useSchedule,
  useDaySlots,
  getDefaultDateRange,
  type ViewMode,
  type AppointmentFiltersState,
} from '@/components/appointments';
import { Button } from '@/components/ui/button';
import { BookingDialog } from '@/scheduling/components/BookingDialog';
import { useBooking } from '@/scheduling/hooks/use-booking';
import type { DaySlot, ComputedSlot, ProviderDaySlots } from '@vagusx/clients';

/** Location of the clicked provider@location day-view column (null for telehealth). */
type DayColumnLocation = ProviderDaySlots['location'];

export default function AppointmentsPage(): React.ReactElement {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();

  // Parse URL params for initial state
  const initialView = (searchParams.view as ViewMode) ?? 'slots';
  const initialProviderIds = searchParams.providers?.split(',').filter(Boolean) ?? [];
  const initialLocationIds = searchParams.locations?.split(',').filter(Boolean) ?? [];
  const initialStatuses =
    searchParams.statuses
      ?.split(',')
      .map(Number)
      .filter((n) => !isNaN(n)) ?? [];
  const initialSearch = searchParams.search ?? '';
  const initialDateParam = searchParams.date;
  const initialDate = initialDateParam
    ? startOfDay(parseISO(initialDateParam))
    : startOfDay(new Date());

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);

  // Date state
  const { startDate: defaultStartDate } = getDefaultDateRange();
  const [ribbonStartDate, setRibbonStartDate] = useState<Date>(defaultStartDate);
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  // Compute end date (30 days from ribbon start)
  const startDate = ribbonStartDate;
  const endDate = addDays(ribbonStartDate, 30);

  // Filter state
  const [filters, setFilters] = useState<AppointmentFiltersState>({
    providerIds: initialProviderIds,
    locationIds: initialLocationIds,
    statuses: initialStatuses,
    search: initialSearch,
  });

  // Fetch schedule data (for calendar/table views)
  const { data, isLoading } = useSchedule({
    startDate,
    endDate,
    providerIds: filters.providerIds.length > 0 ? filters.providerIds : undefined,
    locationIds: filters.locationIds.length > 0 ? filters.locationIds : undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    search: filters.search || undefined,
  });

  // Fetch day slots (for slots view)
  const { data: daySlotsData, isLoading: isDaySlotsLoading } = useDaySlots({
    date: selectedDate,
    providerIds: filters.providerIds.length > 0 ? filters.providerIds : undefined,
    locationIds: filters.locationIds.length > 0 ? filters.locationIds : undefined,
  });

  // The slots grid takes a list of days; this view shows the one selected day.
  const slotsDays = useMemo(
    () => (daySlotsData ? [{ date: selectedDate, data: daySlotsData }] : undefined),
    [daySlotsData, selectedDate]
  );

  const allDays = data?.days ?? [];
  const providers = data?.providers ?? [];

  // Filter days to start from the selected date
  const days = allDays.filter((day) => {
    const dayDate = parseISO(day.date);
    return !isBefore(dayDate, selectedDate);
  });

  // Update URL when state changes
  const updateUrl = useCallback(
    (newParams: Record<string, string | undefined>) => {
      navigate({
        to: '/appointments',
        search: (prev) => {
          const next = { ...prev };
          Object.entries(newParams).forEach(([key, value]) => {
            if (value) {
              (next as Record<string, string | undefined>)[key] = value;
            } else {
              delete (next as Record<string, string | undefined>)[key];
            }
          });
          return next;
        },
        replace: true,
      });
    },
    [navigate]
  );

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
      updateUrl({ view: mode });
    },
    [updateUrl]
  );

  // Handle date selection from ribbon
  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      updateUrl({ date: format(date, 'yyyy-MM-dd') });
    },
    [updateUrl]
  );

  // Handle shifting the date ribbon (only shifts the visible window)
  const handleShiftDates = useCallback((days: number) => {
    setRibbonStartDate((prev) => addDays(prev, days));
  }, []);

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (newFilters: AppointmentFiltersState) => {
      setFilters(newFilters);
      updateUrl({
        providers: newFilters.providerIds.length > 0 ? newFilters.providerIds.join(',') : undefined,
        locations: newFilters.locationIds.length > 0 ? newFilters.locationIds.join(',') : undefined,
        statuses: newFilters.statuses.length > 0 ? newFilters.statuses.join(',') : undefined,
        search: newFilters.search || undefined,
      });
    },
    [updateUrl]
  );

  // Appointment detail sheet state
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  // Booking dialog state
  const [bookingSlot, setBookingSlot] = useState<ComputedSlot | null>(null);
  const { isBooking, book } = useBooking();

  // When arriving from "Schedule from referral", pre-select that patient in the
  // booking dialog (the referral's patient is already linked). Memoized so the
  // dialog's pre-select effect doesn't re-fire on every render and clobber a
  // manual patient change.
  const initialPatient = useMemo(
    () =>
      searchParams.bookPatientId
        ? { id: searchParams.bookPatientId, label: searchParams.bookPatientName ?? '' }
        : null,
    [searchParams.bookPatientId, searchParams.bookPatientName]
  );

  // Handle appointment click
  const handleAppointmentClick = useCallback((appointmentId: string) => {
    setSelectedAppointmentId(appointmentId);
  }, []);

  // Handle slot click (for slots view)
  const handleSlotClick = useCallback(
    (slot: DaySlot, providerId: string, location: DayColumnLocation) => {
      if (slot.status === 'open') {
        // Find provider name from day slots data
        const providerData = daySlotsData?.providers.find((p) => p.provider.id === providerId);
        const providerName = providerData
          ? `${providerData.provider.firstName} ${providerData.provider.lastName}`
          : '';

        // Construct a ComputedSlot from DaySlot + the column's provider/location
        // context. Location comes straight from the clicked provider@location
        // column so the booking commits to the right department (EHR sync needs
        // it). The dialog (calendar mode) still lets staff change it.
        const computedSlot: ComputedSlot = {
          startTime: slot.startTime,
          endTime: slot.endTime,
          durationMinutes: slot.durationMinutes,
          providerId,
          providerName,
          providerDisplayName: providerName,
          providerSpokenName: providerName,
          providerCredential: null,
          locationId: location?.id ?? '',
          locationName: location?.name ?? '',
          locationTimezone: location?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Carry the open slot's appointment type so the booking commits it — the
          // EHR sync needs it (without it the write defers to PENDING).
          appointmentTypeId: slot.appointmentTypeId ?? '',
          appointmentTypeName: slot.appointmentTypeName ?? '',
          // Show the booking time in the location's timezone, matching the grid.
          displayTime: formatInTimeZone(
            slot.startTime,
            location?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            "EEEE, MMMM d 'at' h:mm a"
          ),
        };
        setBookingSlot(computedSlot);
      } else if (slot.appointment) {
        setSelectedAppointmentId(slot.appointment.id);
      }
    },
    [daySlotsData]
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 shrink-0">
        <h1 className="text-lg font-semibold">Appointments</h1>
        <div className="flex items-center gap-2">
          <Link to="/find-slots">
            <Button variant="outline" size="sm" className="gap-1.5">
              <Search className="h-4 w-4" />
              Find Slots
            </Button>
          </Link>
          <ViewToggle value={viewMode} onChange={handleViewModeChange} />
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate({ to: '/setup/appointment-types' })}
            aria-label="Appointment settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="shrink-0">
        <AppointmentFilters filters={filters} onChange={handleFiltersChange} />
      </div>

      {/* Date Ribbon */}
      <div className="shrink-0">
        <DateRibbon
          startDate={startDate}
          daysToShow={30}
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          onShiftDates={handleShiftDates}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {viewMode === 'calendar' && (
          <CalendarView
            days={days}
            providers={providers}
            isLoading={isLoading}
            onAppointmentClick={handleAppointmentClick}
          />
        )}
        {viewMode === 'table' && (
          <TableView
            days={days}
            providers={providers}
            isLoading={isLoading}
            onAppointmentClick={handleAppointmentClick}
          />
        )}
        {viewMode === 'slots' && (
          <SlotsView
            days={slotsDays}
            isLoading={isDaySlotsLoading}
            searchTerm={filters.search}
            onSlotClick={handleSlotClick}
          />
        )}
      </div>

      {/* Appointment Detail Sidebar */}
      <AppointmentDetailSheet
        appointmentId={selectedAppointmentId}
        onClose={() => setSelectedAppointmentId(null)}
      />

      {/* Booking Dialog (from slot click) */}
      <BookingDialog
        open={bookingSlot !== null}
        slot={bookingSlot}
        onClose={() => setBookingSlot(null)}
        onBook={async (data) => {
          const ok = await book({ ...data, bookedVia: 'staff' });
          setBookingSlot(null);
          // Clear the referral pre-select so the next booking starts fresh.
          if (ok && searchParams.bookPatientId) {
            updateUrl({ bookPatientId: undefined, bookPatientName: undefined });
          }
        }}
        isBooking={isBooking}
        canSearchPatients={true}
        mode="calendar"
        initialPatient={initialPatient}
      />
    </div>
  );
}
